import { useState } from "react";
import { FormAssessment } from "@type/assessments";
import { convertContentBreakline } from "@util/string";
import { Button } from "@component/button";
import { Textarea } from "@component/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { cn } from "@util/tailwind";
import { Card, CardContent } from "@component/card";

interface PreviewQuizProps {
  assessment: FormAssessment;
  onSubmit?: () => void;
}

export default function PreviewQuiz({ assessment, onSubmit }: PreviewQuizProps) {
  const [answers, setAnswers] = useState<Record<string, string | number>>({});

  const questions = assessment?.content?.questions || [];

  const handleMultipleChoiceAnswer = (questionId: string, answer: string) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleShortAnswerChange = (questionId: string, answer: string) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleRatingChange = (questionId: string, rating: number) => {
    setAnswers({ ...answers, [questionId]: rating });
  };

  const RatingScale = ({
    rating,
    onRatingChange,
    leftLabel = "Very Dissatisfied",
    rightLabel = "Very Satisfied",
  }: {
    rating: number;
    onRatingChange: (rating: number) => void;
    leftLabel?: string;
    rightLabel?: string;
  }) => {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-400 text-right mr-2">
            {leftLabel}
          </span>
          <div className="hidden sm:flex gap-2">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                disabled
                onClick={() => onRatingChange(value)}
                className={cn(
                  "w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-all hover:scale-105",
                  value === rating
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-400"
                )}
              >
                {value}
              </button>
            ))}
          </div>
          <div className="block sm:hidden">
            <input type="range" min="1" max="5" value={rating} onChange={(e) => onRatingChange(Number(e.target.value))} disabled />
          </div>
          <span className="text-sm text-gray-600 dark:text-gray-400 text-left ml-2">
            {rightLabel}
          </span>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {rating === 0 ? "No rating selected" : `Selected: ${rating}`}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-8 sm:px-16 p-4 sm:p-8 rounded-xl">
      <div className="mb-0">
        <div className="flex items-start sm:items-center flex-col sm:flex-row gap-3 mb-4">
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {assessment.content.title}
          </h1>
          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 whitespace-nowrap">
            {questions.length} Question{questions.length !== 1 ? "s" : ""}
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          {assessment.content.description}
        </p>
      </div>
      <Card className="w-full max-w-4xl">
        <CardContent className="p-6">


      <div className="space-y-8">
        {questions.length > 0 && questions.map((question, index) => (
          <div
            key={index}
            style={{ paddingTop: "8px", paddingBottom: "8px" }}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                <span className="text-xs font-medium text-blue-600 dark:text-gray-400">
                  {index + 1}
                </span>
              </div>
              <div className="flex-1">
                <div className="text-sm sm:text-base font-medium text-gray-900 dark:text-white" dangerouslySetInnerHTML={{ __html: convertContentBreakline(question.content || "") }} />
              </div>
            </div>
            <div className="mt-4 ml-0 sm:ml-10">
              {question.type === "Multiple Choice" && question.options && (
                <Select
                  value={answers[question.id] as string | undefined}
                  onValueChange={(value) => handleMultipleChoiceAnswer(question.id, value)}
                  disabled
                >
                  <SelectTrigger className="w-full" disabled>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {(question.options || []).map((option, idx) => (
                      <SelectItem key={idx} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {question.type === "Short Answer" && (
                <Textarea
                  value={answers[question.id] as string | undefined}
                  onChange={(e) => handleShortAnswerChange(question.id, e.target.value)}
                  className="min-h-[100px] resize-y"
                  disabled
                />
              )}
              {question.type === "Rating Scale" && (
                <RatingScale
                  rating={answers[question.id] as string | undefined ? Number(answers[question.id]) : 0}
                  onRatingChange={(rating) => handleRatingChange(question.id, rating)}
                  leftLabel={question.min_label || "Beginner"}
                  rightLabel={question.max_label || "Expert"}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      <hr className="mt-8 border-t border-gray-200 dark:border-gray-700" />
      <div className="mt-4 sm:mt-8 space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400" style={{ color: "#71717a" }}>
          Please review your answers carefully; you cannot change your form responses once submitted.
        </p>
        <div className="flex justify-end">
          <Button
            onClick={onSubmit}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2 rounded-lg"
            style={{ paddingRight: "24px", paddingLeft: "24px", borderRadius: "0.375rem" }}
          >
            Submit Answers
          </Button>
        </div>
      </div>
        </CardContent>
      </Card>
    </div>
  );
}
